import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import { API_POST, DBRequest, useConfig } from '../../../api/api';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../api/useFetch';
import SkeletonCard from '../../../components/cards/SkeletonCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { OrderStatus } from '../../../components/special/Status';
import PreviewPositions from '../../../components/preview/PreviewPositions';
import NewProjectPositionsRelation from '../project-orders/NewProjectPositionsRelation';
import ExoDialog from '../../../components/exo/ExoDialog';
import { useState } from 'react';
import Loader from '../../../components/special/Loader';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../../components/exo/ExoTableToolbar';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NoDataAnimation from '../../../components/animation/NoDataAnimation';
import DateLabel from '../../../components/label/DateLabel';
import MoneyLabel from '../../../components/label/MoneyLabel';
import { round } from '../../../components/special/round';
import { IconButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const OrdersTab = ({ project, onDelete, onChange }) => {
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, isLoading, setIsLoading, reloadApiData } = useFetch(
    `projects/${project.id}/orders`
  );
  const [open, setOpen] = useState(false);

  function handleRemove(positions) {
    var positionIds = [];
    positions.forEach((element) => {
      positionIds.push(element.id);
    });
    const requestData = {
      relationType: 'orderPositions',
      mode: 'remove',
      relations: positionIds,
    };
    DBRequest({
      config,
      path: `projects/${project.id}/relations`,
      method: API_POST(requestData),
      onResponse: reloadApiData,
      onLoading: setIsLoading,
    });
  }

  return (
    <Box className="w-full flex flex-col relative">
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('Add Order')}
          icon={<AddCircleOutlineOutlinedIcon />}
          onClick={() => setOpen(true)}
        />
      </ExoTableToolbar>
      <Loader active={isLoading} />
      {apiData && apiData.data.length > 0 && (() => {
        const totalNetto = apiData.data.reduce((sum, order) => sum + (order.amount || 0), 0);
        const totalBrutto = apiData.data.reduce((sum, order) => {
          const orderBrutto = (order.positions || []).reduce((posSum, pos) => {
            const net = round(pos.amount * pos.price, 2);
            return posSum + net * (1 + (pos.taxRate || 0) / 100);
          }, 0);
          return sum + orderBrutto;
        }, 0);
        const lastDate = [...apiData.data].sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.date;
        return (
          <Box className="flex gap-4 px-2 pb-2 flex-wrap">
            <Typography variant="body2" color="text.secondary">
              {t('Last Order')}: <DateLabel date={lastDate} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('netto')}: <MoneyLabel money={round(totalNetto, 2)} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('brutto')}: <MoneyLabel money={round(totalBrutto, 2)} />
            </Typography>
          </Box>
        );
      })()}
      {!apiData ? (
        <SkeletonCard height="55" />
      ) : (
        <>
          {apiData.data.length === 0 && (
            <Box className="w-full flex justify-center">
              <NoDataAnimation />
            </Box>
          )}
          {apiData.data.map((data) => (
            <Order key={data.id} data={data} onRemove={handleRemove} />
          ))}
        </>
      )}
      <ExoDialog open={open}>
        <NewProjectPositionsRelation
          project={project}
          onClose={() => setOpen(false)}
          reloadApiData={reloadApiData}
        />
      </ExoDialog>
    </Box>
  );
};

const Order = ({ data, onRemove }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Accordion
      sx={{
        '& .order-link-btn': { opacity: 0, transition: 'opacity 0.2s' },
        '&:hover .order-link-btn': { opacity: 1 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="flex justify-between items-center w-full pr-4">
          <Box className="flex items-center gap-2">
            {data.nr && (
              <Typography component="span" sx={{ fontWeight: 'bold', opacity: 0.6 }}>
                {data.nr}
              </Typography>
            )}
            <Typography component="span">{data.title}</Typography>
            {data.date && (
              <Typography component="span" variant="caption" sx={{ opacity: 0.6 }}>
                <DateLabel date={data.date} />
              </Typography>
            )}
            <IconButton
              className="order-link-btn"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/orders/${data.id}`);
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ maxWidth: '200px' }}>
            <OrderStatus number={data.status} />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <PreviewPositions positions={data.positions} />
        <Box className="mt-2">
          <ExoTableToolbarItem
            text={t('Remove Order')}
            icon={<RemoveCircleIcon />}
            onClick={() => onRemove(data.positions)}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default OrdersTab;
